// src/pages/admin/AdminDashboard.jsx
import React from "react";
import {
  FaShoppingCart,
  FaUsers,
  FaBoxOpen,
  FaChartLine,
  FaPlus,
  FaSignOutAlt,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import Order from "./Order";
import { useOrder, useProducts } from "../../hooks/usehooks";

const StatCard = ({ to, icon: Icon, iconClassName, label, value }) => {
  const CardInner = (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5 flex items-center gap-3 sm:gap-4 hover:shadow transition-shadow">
      <div className="shrink-0">
        <Icon className={`text-2xl sm:text-3xl ${iconClassName}`} />
      </div>

      <div className="min-w-0">
        <p className="text-xs sm:text-sm text-gray-500 leading-5">{label}</p>
        {value !== undefined && value !== null && (
          <p className="text-lg sm:text-xl font-semibold text-gray-800 truncate">
            {value}
          </p>
        )}
      </div>
    </div>
  );

  if (to) return <Link to={to} className="block">{CardInner}</Link>;
  return CardInner;
};

const AdminDashboard = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {data} = useProducts()
  const {data: ordersData} = useOrder()
console.log("Orders data in AdminDashboard:", ordersData);
  const { mutate: logout, isPending } = useMutation({
    mutationKey: ["auth", "logout"],
    mutationFn: async () => {
      const token = Cookies.get("gameport"); // ✅ read token (don’t remove yet)

      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/signout`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Something went wrong");
      return data;
    },
    onSuccess: () => {
      Cookies.remove("gameport"); // ✅ remove cookie AFTER success
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      navigate("/signin");
    },
  });

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-5 sm:py-8">
        {/* Header */}
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
              Admin Dashboard
            </h1>
            <p className="text-sm text-gray-500">
              Manage products, orders, and site content.
            </p>
          </div>

          <button
            type="button"
            onClick={() => logout()}
            disabled={isPending}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-red 600 px-4 py-2.5 text-white shadow-sm hover:bg-red 700 active:bg-red 800 disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            <FaSignOutAlt />
            {isPending ? "Logging out..." : "Logout"}
          </button>
        </header>

        {/* Stats */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-8">
          <StatCard
            to="/admin/create-product"
            icon={FaPlus}
            iconClassName="text-blue-600"
            label="Add Product"
          />

          <StatCard
            to="/admin/create-hero"
            icon={FaPlus}
            iconClassName="text-blue-600"
            label="Add Hero"
          />

          <StatCard
          to="/all-orders"
            icon={FaShoppingCart}
            iconClassName="text-blue-600"
            label="Orders"
            value={ordersData?.orders?.length ?? 0}
          />

          {/* <StatCard
          to="/customers"
            icon={FaUsers}
            iconClassName="text-green-600"
            label="Customers"
            value="860"
          /> */}

          <StatCard
            to="/consoles"
            icon={FaBoxOpen}
            iconClassName="text-purple-600"
            label="Products"
            value= {data?.products?.length ?? 0}
          />

          <StatCard
            icon={FaChartLine}
            iconClassName="text-orange-600"
            label="Revenue"
            value="₦4.2M"
          />
        </section>

        {/* Orders */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800">
              Recent Orders
            </h2>
          </div>

          <div className="overflow-x-auto">
            <Order />
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
